import React, {useState} from 'react';
import {Box, Button, CircularProgress, DialogContent, IconButton, InputAdornment, TextField, Dialog} from '@mui/material';
import {getDownloadURL, ref, uploadBytes} from "firebase/storage";
import {addDoc, collection, getDocs, query, where, doc, getDoc} from "firebase/firestore";
import {db, SECRETS_ENV, storage} from '../../../Config/firebase';
import CloseIcon from '@mui/icons-material/Close';
import {toast} from "react-toastify";
import {RootState, useAppDispatch, useAppSelector} from "../../../state/state.tsx";
import {closeModal, toggleLoading} from "../../../state/ModalSlice/ModalSlice.ts";
import { selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2.ts';

export default function CustomBrandingModal() {
    const [subdomain, setSubdomain] = useState<string>('');
    const [subdomainMessage, setSubdomainMessage] = useState<string | null>(null);
    const [subDomainError, setSubDomainError] = useState<boolean>(false);
    const [tagline, setTagline] = useState<string>('');
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [brandingImageFile, setBrandingImageFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [brandingImagePreview, setBrandingImagePreview] = useState<string | null>(null);
    const [logoImageSizeExceeded, setLogoImageSizeExceeded] = useState<boolean>(false);
    const [brandingImageSizeExceeded, setBrandingImageSizeExceeded] = useState<boolean>(false)
    const [customWhatsAppChannel, setCustomWhatsAppChannel] = useState<string>('');
    const [customNumber, setCustomNumber] = useState<string>('');
    const [optinUrl, setOptinUrl] = useState<string>('');
    

    

    // Reset States

    function ResetStates() {
        setSubdomain("");
        setSubdomainMessage(null);
        setSubDomainError(false);
        setTagline("");
        setLogoFile(null);
        setLogoPreview(null)
        setBrandingImageFile(null);
        setBrandingImagePreview(null);
        setBrandingImageSizeExceeded(false);
        setLogoImageSizeExceeded(false)
        setCustomWhatsAppChannel('');
        setCustomNumber('');
        setOptinUrl('');
        
    }


    // Modal Handlers

    const Dispatch = useAppDispatch()

    const isLoading = useAppSelector((state: RootState) => state.ModalSlice.isLoading)

    const open = useAppSelector((state: RootState) => state.ModalSlice.CustomBrandingModalOpen)

    const providerReference = useAppSelector(selectEffectiveUID)  

    const userMobileNumber = useAppSelector((state: RootState) => state.userSliceV2.data?.phone_number || '')

    
    const handleLogoUpload = async (): Promise<string> => {
        if (logoFile) {
            const storageRef = ref(storage, `logos/${providerReference}/${logoFile.name}`);
            await uploadBytes(storageRef, logoFile);
            return getDownloadURL(storageRef);
        }
        return '';
    };

    function onClose() {
        ResetStates();
        Dispatch(closeModal("CustomBrandingModalOpen"));
    }

    // ------------------------Handle Change Form States

    const handleBrandingImageUpload = async (): Promise<string> => {
        if (brandingImageFile) {
            const storageRef = ref(storage, `brandingImages/${providerReference}/${brandingImageFile.name}`);
            await uploadBytes(storageRef, brandingImageFile);
            return getDownloadURL(storageRef);
        }
        return '';
    };
    const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files ? event.target.files[0] : null;
        setLogoImageSizeExceeded(false)
        if (file) {
            if (file.size > 5 * 1024 * 1024) {

                setLogoImageSizeExceeded(true)
                return;
            }
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };


    const handleChangeCustomNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
        const {value} = event.target;
        // -------------Function to handle number inputs
        if (value.length > 12) {
            return;
        }

        if (!value) {
            setCustomNumber("")
            return;
        }

        for (let i = 0; i < value.length; i++) {
            const char = value[i];
            if (char >= '0' && char <= '9') { /* empty */
            } else {
                return;
            }
        }
        setCustomNumber(value)
    }


    const handleBrandingImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files ? event.target.files[0] : null;
        setBrandingImageSizeExceeded(false)
        if (file) {
            if (file.size > 5 * 1024 * 1024) {

                setBrandingImageSizeExceeded(true)
                return;
            }
            setBrandingImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setBrandingImagePreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };


    const domain = SECRETS_ENV.DOMAIN;

    const checkSubdomainAvailability = async (): Promise<boolean> => {
        // Check for excluding domains
        if (SECRETS_ENV.EXCLUDED_DOMAINS.includes(subdomain)) {
            return false
        }

        const subdomainQuery = query(collection(db, 'CustomBranding'), where('subdomain', '==', `${subdomain}.${domain}`));
        const querySnapshot = await getDocs(subdomainQuery);
        return querySnapshot.empty;
    };

    // Fetch already exisiting admins and others

    const fetchDelegatedRoles = async (): Promise<string[]> => {
        try {
            const providerDoc = await getDoc(doc(db, "ServiceProviders", providerReference));
            if (providerDoc.exists()) {
                const data = providerDoc.data();
                if (data.delegatedRoles && Array.isArray(data.delegatedRoles)) {
                    return data.delegatedRoles.map((members: any) => members.number);
                }
            }
        } catch (error) {
            console.error("Error fetching delegated roles:", error);
        }
        return [];
    };



    //--------------- onSubmit

    const handleSubmit = async (e: any) => {
        e.preventDefault()
        
        Dispatch(toggleLoading(true))
      
        try {
            const isAvailable = await checkSubdomainAvailability();

            if (!isAvailable) {
                setSubdomainMessage('This subdomain is already taken.');
                return;
            }

            let logoUrl = "";
            let brandingImageUrl = "";
            if(logoFile){
                 logoUrl = await handleLogoUpload();
            }
            if(brandingImageFile){
                 brandingImageUrl = await handleBrandingImageUpload();
            }
          
           
            const delegatedRoleNumbers = await fetchDelegatedRoles();

            const brandingData = {
                providerReference,
                subdomain: `${subdomain}.${domain}`,
                tagline,
                logo: logoUrl,
                brandingImage: brandingImageUrl,
                authorizedNumbers: [userMobileNumber, ...delegatedRoleNumbers],
                customWhatsAppChannel,
                customNumber,
                optinUrl,
            };

            await addDoc(collection(db, "CustomBranding"), brandingData);


            toast.success('Custom Branding details saved successfully!');
            onClose();
        } catch (error) {
            console.error("Error saving custom branding details: ", error);
            toast.error('Failed to save custom branding details. Please try again.')
        }
        Dispatch(toggleLoading(false))
    };

   return (<Dialog
        sx={{
            padding: 0,
            '& .MuiPaper-root': {
                borderRadius: '10px',
            },
        }}
        maxWidth="sm"
        fullWidth
        open={open}
        PaperProps={{
            component: 'div',
        }}>
        <DialogContent sx={{
            paddingX: { xs: "16px", sm: "24px", md: "60px" }, 
            paddingY: { xs: "30px", sm: "40px", md: "50px" }
        }} className={"bg-white"}>
            <div className={"w-full max-w-[550px] mx-auto"}>
                <Box sx={{width: '100%', display: 'flex', justifyContent: 'space-between', mb: 2}}>
                    <div className={"header pb-3 sm:pb-[20px]"}>
                        <p className={"text-lg sm:text-cf_heading font-[600] "}>Custom Branding</p>
                        <p className={"text-xs sm:text-cf_small font-[400]"}>Please fill all the
                            fields.</p>
                    </div>
                    <div className={"absolute right-2 sm:right-4 top-2 sm:top-4"}><IconButton onClick={onClose} sx={{ padding: { xs: '4px', sm: '8px' } }}>
                        <CloseIcon sx={{ fontSize: { xs: '18px', sm: '24px' } }} />
                    </IconButton></div>

                </Box>
                <div className={"flex flex-col gap-4"}>
                    <TextField
                        label="Subdomain"
                        variant="outlined"
                        fullWidth
                        size={"small"}
                        value={subdomain}
                        onChange={(e) => {
                            const value = e.target.value.toLowerCase().replace(/\./g, '');
                            setSubdomain(value);
                        }}
                        placeholder="Enter subdomain"
                        helperText={subdomainMessage ? subdomainMessage : ""}
                        error={subDomainError}
                        InputProps={{
                            endAdornment: <InputAdornment position="end">.{domain}</InputAdornment>,
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: 'grey', borderRadius: '10px',
                                }, '&:hover fieldset': {
                                    borderColor: 'grey',
                                }, '&.Mui-focused fieldset': {
                                    borderColor: 'skyblue',
                                }, '& .MuiInputBase-input': {
                                    fontSize: '14px',
                                },
                            }, '& .MuiFormHelperText-root': {
                                color: subDomainError ? 'red' : 'green',
                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            }, fontWeight: 400, width: '100%',
                        }}
                        onBlur={async () => {
                            if (subdomain.trim() !== "") {
                                const isAvailable = await checkSubdomainAvailability();
                                if (!isAvailable) {
                                    setSubdomainMessage('This subdomain is already taken.');
                                    setSubDomainError(true)
                                } else {
                                    setSubdomainMessage(`${subdomain}.${domain} is available`);
                                    setSubDomainError(false)
                                }
                            }
                        }}
                    />


                    <Button
                        variant="outlined"
                        component="label"
                        onClick={async () => {
                            if (subdomain.trim() !== "") {
                                const isAvailable = await checkSubdomainAvailability();
                                if (!isAvailable) {
                                    setSubdomainMessage('This subdomain is already taken.');
                                    setSubDomainError(true)
                                } else {
                                    setSubdomainMessage(`${subdomain}.${domain} is available`);
                                    setSubDomainError(false)
                                }
                            }
                        }}
                        fullWidth
                        sx={{
                            mb: 2,
                            borderRadius: '8px',
                            textTransform: 'none',
                            color: '#1D4ED8',
                            borderColor: '#1D4ED8',
                            '&:hover': {
                                backgroundColor: '#1D4ED8', color: "#FFFFFF"
                            },
                        }}
                    >
                        Check Availability

                    </Button>

                    <TextField
                        label="Tagline (optional)"
                        variant="outlined"
                        fullWidth
                        value={tagline}
                        size={"small"}
                        onChange={(e) => setTagline(e.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: 'grey', borderRadius: '10px'// Default border color
                                }, '&:hover fieldset': {
                                    borderColor: 'grey', // Border color on hover
                                }, '&.Mui-focused fieldset': {
                                    borderColor: 'skyblue', // Border color when focused
                                }, '& .MuiInputBase-input': {
                                    fontSize: '14px', // Setting font size
                                },
                            }, fontWeight: 400, width: '100%'
                        }}
                    />

                    <TextField
                        label="Custom WhatsApp Channel"
                        variant="outlined"
                        fullWidth
                        size={"small"}
                        value={customWhatsAppChannel}
                        disabled={true}
                        placeholder='918610203336'
                        onChange={(e) => setCustomWhatsAppChannel(e.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: 'grey',
                                    borderRadius: '10px'
                                },
                                '&:hover fieldset': {
                                    borderColor: 'grey',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'skyblue',
                                },
                                '& .MuiInputBase-input': {
                                    fontSize: { xs: '14px', sm: '14px' },
                                },
                            },
                            fontWeight: 400,
                            width: '100%'
                        }}
                    />

                    <TextField
                        label="Custom Number"
                        variant="outlined"
                        fullWidth
                        size={"small"}
                        disabled={true}
                        value={customNumber}
                        helperText={"If you wish to add a Custom Channel for your brand, please contact our Support team at support@hosaksham.com."}
                        onChange={handleChangeCustomNumber}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: 'grey',
                                    borderRadius: '10px'
                                },
                                '&:hover fieldset': {
                                    borderColor: 'grey',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'skyblue',
                                },
                                '& .MuiInputBase-input': {
                                    fontSize: { xs: '14px', sm: '14px' },
                                },
        
                            },
                            '& .MuiFormHelperText-root': {
                                color: subDomainError ? 'red' : 'green',
                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                '&.Mui-disabled': {
                                    color: 'red', // Ensures the helper text is red even when disabled
                                },
                            },
                            fontWeight: 400,
                            width: '100%'
                        }}
                    />

                    <TextField
                        label="Optin URL"
                        variant="outlined"
                        fullWidth
                        size={"small"}
                        value={optinUrl}
                        onChange={(e) => setOptinUrl(e.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: 'grey',
                                    borderRadius: '10px'
                                },
                                '&:hover fieldset': {
                                    borderColor: 'grey',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'skyblue',
                                },
                                '& .MuiInputBase-input': {
                                    fontSize: { xs: '14px', sm: '14px' },
                                },
                            },
                            fontWeight: 400,
                            width: '100%'
                        }}
                    />

                    <div><Button
                        variant="outlined"
                        component="label"
                        fullWidth
                        sx={{
                            mb: 0,
                            borderRadius: '8px',
                            textTransform: 'none',
                            color: '#1D4ED8',
                            borderColor: '#1D4ED8',
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            py: { xs: 0.75, sm: 1 },
                            '&:hover': {
                                backgroundColor: '#1D4ED8', color: "#FFFFFF"
                            },
                        }}
                    >
                        <span className="hidden sm:inline">Upload Logo</span><span className="sm:hidden">Upload Logo</span> <span className={"text-xs sm:text-sm ml-2"}>(Max 5mb)</span>
                        <input
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={handleLogoChange}
                        />
                    </Button><p className={"text-xs mt-2.5 text-center"}>Preferred size 2900 * 1200</p></div>
                    {logoImageSizeExceeded &&
                        <p className={"text-cc_error mb-2 text-xs sm:text-sm"}>File is too large, please select a file less than
                            5MB.</p>}
                    {logoPreview && (<Box sx={{mb: 2, position: 'relative', width: { xs: 80, sm: 100 }, height: { xs: 80, sm: 100 }}}>
                        <img src={logoPreview} alt="Logo preview"
                             style={{width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px'}}/>
                        <IconButton
                            onClick={() => {
                                setLogoFile(null);
                                setLogoPreview(null);
                            }}
                            sx={{position: 'absolute', top: 4, right: 4, bgcolor: 'background.paper', padding: { xs: '4px', sm: '8px' }}}
                        >
                            <CloseIcon sx={{ fontSize: { xs: '16px', sm: '18px' } }} />
                        </IconButton>
                    </Box>)}

                    <div>
                        <Button
                            variant="outlined"
                            component="label"
                            fullWidth
                            sx={{
                                mb: 0,
                                borderRadius: '8px',
                                textTransform: 'none',
                                color: '#1D4ED8',
                                borderColor: '#1D4ED8',
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                py: { xs: 0.75, sm: 1 },
                                '&:hover': {
                                    backgroundColor: '#1D4ED8', color: "#FFFFFF"
                                },
                            }}
                        >
                            <span className="hidden sm:inline">Upload Marketing Image</span><span className="sm:hidden">Upload Image</span> <span className={"text-xs sm:text-sm ml-2"}>(Max 5mb)</span>
                            <input
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={handleBrandingImageChange}
                            />
                        </Button>
                        <p className={"text-xs mt-2.5 text-center"}>Preferred size 1600px * 1600px</p></div>
                    {brandingImageSizeExceeded &&
                        <p className={"text-cc_error mb-2 text-xs sm:text-sm"}>File is too large, please select a file less than
                            5MB.</p>}
                    {brandingImagePreview && (<Box sx={{mb: 2, position: 'relative', width: { xs: 80, sm: 100 }, height: { xs: 80, sm: 100 }}}>
                        <img src={brandingImagePreview} alt="Branding Image preview" style={{width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px'}}/>
                        <IconButton
                            onClick={() => {
                                setBrandingImageFile(null);
                                setBrandingImagePreview(null);
                            }}
                            sx={{position: 'absolute', top: 4, right: 4, bgcolor: 'background.paper', padding: { xs: '4px', sm: '8px' }}}
                        >
                            <CloseIcon sx={{ fontSize: { xs: '16px', sm: '18px' } }} />
                        </IconButton>
                    </Box>)}
                </div>

                <div className={"flex flex-col sm:flex-row gap-2 pt-3 sm:pt-4"}>
                    <button disabled={isLoading} onClick={(e) => {
                        e.preventDefault();
                        onClose()
                    }}
                            className={`w-full bg-cc_bg-tertiary border border-solid border-gray-500 p-1.5 rounded-lg text-xs sm:text-sm ${(isLoading) ? "cursor-not-allowed" : "cursor-pointer"}`}>Cancel
                    </button>


                    <button
                        onClick={handleSubmit}
                        disabled={!subdomain.trim()  || isLoading}
                        className={`w-full bg-cc_brand text-white p-1.5 rounded-lg border-gray-500 flex justify-center items-center text-xs sm:text-sm ${(!subdomain.trim() ||  isLoading) ? "cursor-not-allowed" : "cursor-pointer"} `}>
                        {isLoading && <CircularProgress sx={{color: "white", width: '16px !important', height: '16px !important'}} />}
                        <p className={"ml-2"}>{isLoading ? "Loading" : "Confirm"}</p>
                    </button>

                </div>

            </div>
        </DialogContent>
    </Dialog>);
}